import { EntityType } from '@shared/models/entity-enum';
import { GeoPolygonType } from '../esri-map/esri-map.model';
import { GeographyCriteria } from './search-filters.model';

/** To ensure we always have an array */
export const sanitizeGeographyInput = ({
  markets,
  propertyTypes,
  submarkets,
  districts,
  neighborhoods,
}: GeographyCriteria) => ({
  markets: markets || [],
  propertyTypes: propertyTypes || [],
  submarkets: submarkets || [],
  districts: districts || [],
  neighborhoods: neighborhoods || [],
});

/**
 * Returns if a filter type is a supported GeoPolygon
 * @param filter
 */
export const isGeoPolygon = (filter: { type: string }) =>
  filter.type === GeoPolygonType.geoDistance || filter.type === GeoPolygonType.geoPolygon;

/**
 * Returns if a filter type that ARE NOT GeoPolygons
 * @param filter
 */
export const isNotGeoPolygon = filter => !isGeoPolygon(filter);

const parserMap = {
  integer: parseInt,
  numeric: parseFloat,
};

const parseValues = ({ dataType, values }) => {
  const parser = parserMap[dataType];
  let parsedValues = values;
  if (parser != null) {
    parsedValues = Array.isArray(values) ? values.map(val => parser(val)) : parser(values);
  }
  return parsedValues;
};

export const filterValue = ({ type, field, key, dataType, values, value: name, condition, persistentSelection }) =>
  /** NOT NULL, UNDEFINED OR EMPTY STRING (zero, falze allowed as values) AND NOT an array */
  (values != null && values !== '' && !Array.isArray(values)) ||
  /** OR IF Array and is not empty */
  (Array.isArray(values) && values.length > 0)
    ? /** RETURN VALUES */
      {
        type,
        field,
        key,
        values: parseValues({ dataType, values }),
        dataType,
        name,
        condition,
        ...(persistentSelection && { persistentSelection }),
      }
    : /** OTHERWISE, SEND EMPTY (setting null has to be read as a "user's intention to clear a value") */
      { type, field, key, dataType, name, condition, values: null };

export function parseFromToValues({ dataType, values: { from, to } }) {
  let parsedValues = {};
  if (!!from && !!to) {
    parsedValues = {
      from: parseValues({ dataType, values: from }),
      to: parseValues({ dataType, values: to }),
    };
  } else if (!!from) {
    parsedValues = { from: parseValues({ dataType, values: from }) };
  } else if (!!to) {
    parsedValues = { to: parseValues({ dataType, values: to }) };
  }
  return parsedValues;
}

export const filterRange = ({ type, field, key, dataType, values, value: name }) => {
  // enumSelection is used only for dates ranges to store the selected period by the user
  let enumSelectionObj = {};

  if (values?.from && values?.to) {
    enumSelectionObj = { enumSelection: values.enumSelection };
  }
  return values && (values.to || values.from)
    ? { type, field, key, values: parseFromToValues({ dataType, values }), ...enumSelectionObj, dataType, name }
    : { type, field, key, dataType, name };
};

export const getHiddenPropertyFieldByEntity = (entity: EntityType): string => {
  switch (entity) {
    case EntityType.Tenancy:
      return 'property_miq_record_hide_f';
    default:
      return 'property.miq_record_hide_f';
  }
};
